import Papa from 'papaparse';
import { blobToBytes } from '../../core/blob-bytes';
import { throwIfAborted, type Converter } from '../../core/converter';
import { EngineFailure } from '../../core/errors';
import { canonicalExtension, buildOutputName } from '../../core/filename';
import { extensionOf } from '../../core/file-detect';
import { documentFidelity } from '../../core/fidelity';
import { LOCAL_CONVERSION_COST } from '../../core/pricing';
import type { ConversionResult, ConverterCapabilities, ConvertOptions, FileMetadata } from '../../core/types';
import { verifyDocumentOutput } from '../../core/verify';
import { parseDelimited, parseJson, stableStringify } from './structured-codec';

export const SPREADSHEET_SOURCE_FORMATS = ['xlsx', 'xls'];
export const SPREADSHEET_TARGET_FORMATS = ['csv', 'json'];

const MIME_BY_FORMAT: Record<string, string> = {
  csv: 'text/csv',
  json: 'application/json',
};

/** SheetJS 的最小接口面：只声明用到的成员，测试可注入假实现。 */
export interface SheetJsWorkbook {
  SheetNames: string[];
  Sheets: Record<string, unknown>;
}

export interface SheetJsLike {
  read(data: Uint8Array, options: { type: 'array'; raw?: boolean; cellDates?: boolean }): SheetJsWorkbook;
  utils: {
    sheet_to_json(sheet: unknown, options: { header: 1; blankrows: boolean; defval: string }): unknown[][];
  };
}

export interface SpreadsheetConverterDeps {
  /** 动态加载 SheetJS（默认 import('xlsx')）；测试注入假实现以覆盖错误分支。 */
  loadSheetJs?: () => Promise<SheetJsLike>;
}

async function defaultLoadSheetJs(): Promise<SheetJsLike> {
  const module = (await import('xlsx')) as unknown as SheetJsLike;
  return module;
}

/** 表头去重：空表头补 `columnN`，重名追加序号，避免 JSON 键互相覆盖。 */
function normalizeFields(rawHeader: string[]): string[] {
  const seen = new Map<string, number>();
  return rawHeader.map((cell, index) => {
    const base = cell.trim() || `column${index + 1}`;
    const count = seen.get(base) ?? 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base}_${count + 1}`;
  });
}

function toObjects(rows: string[][]): Array<Record<string, string>> {
  const [header, ...body] = rows;
  const fields = normalizeFields(header ?? []);
  return body.map((row) => {
    const record: Record<string, string> = {};
    fields.forEach((field, index) => {
      record[field] = row[index] ?? '';
    });
    return record;
  });
}

/**
 * 电子表格 → CSV / JSON（纯本地，零上传）。
 *
 * 多工作表：CSV 只能承载一张表，默认导出第一张并在保真度报告里写明被丢弃的表；
 * JSON 承载全部工作表（单表导出为数组，多表导出为「表名 → 行对象数组」），因此不丢数据。
 */
export class SpreadsheetDocumentConverter implements Converter {
  private readonly loadSheetJs: () => Promise<SheetJsLike>;

  constructor(deps: SpreadsheetConverterDeps = {}) {
    this.loadSheetJs = deps.loadSheetJs ?? defaultLoadSheetJs;
  }

  capabilities(): ConverterCapabilities {
    return {
      id: 'document.spreadsheet',
      category: 'document',
      sourceFormats: [...SPREADSHEET_SOURCE_FORMATS],
      targetFormats: [...SPREADSHEET_TARGET_FORMATS],
      local: true,
      cloud: false,
      defaultFidelity: 'L3',
      mode: 'local',
      requiresUpload: false,
      estimatedCost: LOCAL_CONVERSION_COST,
    };
  }

  canConvert(source: FileMetadata, targetExtension: string): boolean {
    const sourceExtension = canonicalExtension(source.type.extension);
    const target = canonicalExtension(targetExtension);
    return SPREADSHEET_SOURCE_FORMATS.includes(sourceExtension) && SPREADSHEET_TARGET_FORMATS.includes(target);
  }

  async inspect(file: File): Promise<FileMetadata> {
    // 检视轻量：不解析整本工作簿（大文件解析耗时可观），只做可读性校验交给 convert 阶段。
    return {
      name: file.name,
      size: file.size,
      type: {
        extension: extensionOf(file.name),
        mime: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        category: 'document',
      },
    };
  }

  async convert(file: File, options: ConvertOptions = {}, context?: Parameters<Converter['convert']>[2]): Promise<ConversionResult> {
    const target = canonicalExtension(String(options.targetFormat ?? 'csv'));
    if (!SPREADSHEET_TARGET_FORMATS.includes(target)) {
      throw new EngineFailure('UNSUPPORTED_CONVERSION', `表格引擎不支持输出 ${target} 格式`);
    }

    throwIfAborted(context);
    context?.onProgress?.({ progress: 15, stage: '解析工作簿' });
    const bytes = await blobToBytes(file);

    let workbook: SheetJsWorkbook;
    let sheetJs: SheetJsLike;
    try {
      sheetJs = await this.loadSheetJs();
      workbook = sheetJs.read(bytes, { type: 'array', raw: true, cellDates: false });
    } catch (error) {
      throw new EngineFailure(
        'MALFORMED_FILE',
        `工作簿解析失败：${error instanceof Error ? error.message : String(error)}`,
      );
    }

    const sheetNames = workbook.SheetNames ?? [];
    if (sheetNames.length === 0) throw new EngineFailure('MALFORMED_FILE', '工作簿里没有任何工作表');

    const requested = typeof options.sheetName === 'string' && options.sheetName.trim() ? options.sheetName.trim() : null;
    if (requested && !sheetNames.includes(requested)) {
      throw new EngineFailure('UNSUPPORTED_CONVERSION', `工作簿里没有名为「${requested}」的工作表`);
    }

    throwIfAborted(context);
    context?.onProgress?.({ progress: 50, stage: '转换' });
    const readSheet = (name: string): string[][] => {
      const sheet = workbook.Sheets[name];
      if (!sheet) throw new EngineFailure('MALFORMED_FILE', `工作表「${name}」无法读取`);
      return sheetJs.utils
        .sheet_to_json(sheet, { header: 1, blankrows: false, defval: '' })
        .map((row) => (row ?? []).map((cell) => (cell === null || cell === undefined ? '' : String(cell))));
    };

    const sourceExtension = SPREADSHEET_SOURCE_FORMATS.includes(canonicalExtension(extensionOf(file.name)))
      ? canonicalExtension(extensionOf(file.name))
      : 'xlsx';

    let output: string;
    let sheetsExported = 1;
    if (target === 'csv') {
      const sheetName = requested ?? sheetNames[0];
      const rows = readSheet(sheetName);
      if (rows.length === 0) throw new EngineFailure('MALFORMED_FILE', `工作表「${sheetName}」里没有可导出的数据`);
      const csv = Papa.unparse(rows, { newline: '\n' });
      output = csv.endsWith('\n') ? csv : `${csv}\n`;

      throwIfAborted(context);
      context?.onProgress?.({ progress: 80, stage: '校验' });
      const verification = verifyDocumentOutput({
        parsed: output,
        assertions: buildCsvAssertions(output, rows),
      });
      const fidelity = documentFidelity({
        sourceExtension,
        targetExtension: target,
        structurePreserved: sheetNames.length === 1,
        sheetsTotal: sheetNames.length,
        sheetsExported: 1,
        notes: sheetNames.length > 1 ? [`已导出工作表「${sheetName}」`] : [],
      });
      return this.finish(output, target, file, fidelity, verification);
    }

    const selected = requested ? [requested] : sheetNames;
    sheetsExported = selected.length;
    const payload =
      selected.length === 1
        ? toObjects(readSheet(selected[0]))
        : Object.fromEntries(selected.map((name) => [name, toObjects(readSheet(name))]));
    output = `${stableStringify(payload)}\n`;

    throwIfAborted(context);
    context?.onProgress?.({ progress: 80, stage: '校验' });
    const verification = verifyDocumentOutput({
      parsed: output,
      assertions: buildJsonAssertions(output, selected.length),
    });
    const fidelity = documentFidelity({
      sourceExtension,
      targetExtension: target,
      structurePreserved: true,
      sheetsTotal: sheetNames.length,
      sheetsExported,
      notes: selected.length > 1 ? [`已导出全部 ${selected.length} 张工作表`] : [],
    });
    return this.finish(output, target, file, fidelity, verification);
  }

  private finish(
    output: string,
    target: string,
    file: File,
    fidelity: ConversionResult['fidelity'],
    verification: { passed: boolean; details: string[] },
  ): ConversionResult {
    const mime = MIME_BY_FORMAT[target] ?? 'text/plain';
    const fileName = buildOutputName(file.name, target);
    return {
      blob: new Blob([output], { type: `${mime};charset=utf-8` }),
      fileName,
      metadata: {
        name: fileName,
        size: new TextEncoder().encode(output).length,
        type: { extension: target, mime, category: 'document' },
      },
      fidelity,
      verificationPassed: verification.passed,
      verificationDetails: verification.details,
    };
  }
}

/** CSV 校验：产物必须能被解析器回读，且列数与行数与源工作表一致。 */
function buildCsvAssertions(output: string, rows: string[][]): Array<{ label: string; ok: boolean }> {
  const assertions: Array<{ label: string; ok: boolean }> = [{ label: '产物非空', ok: output.trim().length > 0 }];
  const header = rows[0] ?? [];
  const bodyCount = rows.length - 1;
  if (bodyCount === 0) {
    assertions.push({ label: '仅表头也保留列名', ok: header.every((cell) => output.includes(cell) || cell === '') });
    return assertions;
  }
  try {
    const parsed = parseDelimited(output);
    assertions.push({ label: '产物可被分隔符解析器回读', ok: true });
    assertions.push({ label: '数据行数一致', ok: parsed.rows.length === bodyCount });
    assertions.push({ label: '列数一致', ok: parsed.fields.length === header.length });
  } catch {
    assertions.push({ label: '产物可被分隔符解析器回读', ok: false });
  }
  return assertions;
}

/** JSON 校验：产物必须能被 JSON.parse 回读，且条目数量与导出范围一致。 */
function buildJsonAssertions(output: string, sheetsExported: number): Array<{ label: string; ok: boolean }> {
  const assertions: Array<{ label: string; ok: boolean }> = [{ label: '产物非空', ok: output.trim().length > 0 }];
  try {
    const parsed = parseJson(output);
    if (sheetsExported === 1) {
      assertions.push({ label: '产物为行对象数组', ok: Array.isArray(parsed) });
    } else {
      assertions.push({
        label: '产物覆盖全部工作表',
        ok: parsed !== null && typeof parsed === 'object' && Object.keys(parsed as object).length === sheetsExported,
      });
    }
  } catch {
    assertions.push({ label: '产物可被 JSON.parse 回读', ok: false });
  }
  return assertions;
}
